import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { AuthContext } from "../../../../../context/AuthContext";
import { createDepartmentHoursClassifier } from "../departmentHours";
import { convertDateWithDayName } from "../../../../../scripts/utils";
import { useTranslation } from "react-i18next";

export default function TypeDepartmentSurvey({ data, people, setDataGraph, setFormattedHeader, filteredPosts, user }) {
    const [departmentSurvey, setDepartmentSurvey] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentDepartmentPage, setCurrentDepartmentPage] = useState({});
    const [departmentItemsPerPage, setDepartmentItemsPerPage] = useState(5);
    const itemsPerPage = 5;
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;
    const canUseCallerId = user && user.holdingId === 33;
    const { apiUrl } = useContext(AuthContext);
    const [hoursFilter, setHoursFilter] = useState("all");
    const hoursQuery = useQuery({
        queryKey: ["webexDepartmentHours", apiUrl, user?.entrepriseId],
        queryFn: async () => (await axios.get(`${apiUrl}webex/calling/departmentHours`, { withCredentials: true })).data,
        enabled: Number(user?.entrepriseId) === 41
    });
    const hasSchedule = !!hoursQuery.data?.versions?.length;
    const effectiveHoursFilter = hasSchedule ? hoursFilter : "all";
    const classifyHours = useMemo(() => createDepartmentHoursClassifier(hoursQuery.data), [hoursQuery.data]);

    const normalizedFilteredPosts = useMemo(() => {
        if (Array.isArray(filteredPosts)) {
            return {
                posts: filteredPosts.flatMap(entry => entry.posts || []),
                calltype: filteredPosts[0]?.calltype || "",
                personNames: filteredPosts
                    .map(entry => entry.personName?.trim().toLowerCase())
                    .filter(Boolean)
            };
        }

        return {
            posts: filteredPosts?.posts || [],
            calltype: filteredPosts?.calltype || "",
            personNames: []
        };
    }, [filteredPosts]);

    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    const getPersonName = (person) => (
        `${person?.firstName || ""} ${person?.lastName || ""}`.trim()
    );

    const getDepartmentName = (person) => {
        const department = person?.department || person?.departmentName || person?.department_name;

        if (typeof department === "string" && department.trim()) {
            return department.trim();
        }

        if (department && typeof department === "object") {
            return department.displayName || department.name || department.label || t("Sans departement");
        }

        return t("Sans departement");
    };

    const isBusyCall = (call) => {
        const busyValues = [
            call?.calloutcomereason,
            call?.originalreason,
            call?.redirectreason,
            call?.relatedreason
        ].map(value => String(value || "").toLowerCase());

        return busyValues.some(value => value.includes("busy"));
    };

    const getCallResultLabel = (call) => {
        if (isBusyCall(call)) return "Busy";
        return call?.answerindicator || "No";
    };

    const getCallDirection = (direction) => {
        switch (direction) {
            case "TERMINATING": return t("Entrant");
            case "ORIGINATING": return t("Sortant");
            default: return direction;
        }
    };

    const getCallType = (callType) => {
        switch (callType) {
            case "SIP_ENTERPRISE": return "Interne";
            case "SIP_NATIONAL": return "National";
            case "SIP_MOBILE": return "Mobile";
            case "SIP_INTERNATIONAL": return "International";
            case "SIP_PREMIUM": return "Premium";
            case "SIP_INBOUND": return "Entrant";
            case "SIP_OPERATOR": return "Operateur";
            case "SIP_TOLLFREE": return "Sans frais";
            case "SIP_MEETING": return "Reunion";
            case "UNKNOWN": return "Inconnu";
            default: return callType;
        }
    };

    const peopleByNumber = useMemo(() => {
        const map = new Map();

        (people || []).flat().forEach(person => {
            [person.phone?.work, person.phone?.mobile, person.extension].filter(Boolean).forEach(number => {
                map.set(number, person);
            });
        });

        return map;
    }, [people]);

    const departments = useMemo(() => {
        const map = new Map();
        const selectedPosts = new Set(normalizedFilteredPosts.posts || []);
        const hasPostFilter = selectedPosts.size > 0;

        (people || []).flat().forEach(person => {
            const numbers = [...new Set([person.phone?.work, person.phone?.mobile, person.extension].filter(Boolean))];
            const filteredNumbers = hasPostFilter
                ? numbers.filter(number => selectedPosts.has(number))
                : numbers;

            if (filteredNumbers.length === 0) return;

            const departmentName = getDepartmentName(person);
            if (!map.has(departmentName)) {
                map.set(departmentName, {
                    department: departmentName,
                    persons: [],
                    numbers: new Set(),
                    Outgoing: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0, busy: 0 },
                    Incoming: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0, busy: 0 },
                    Total: { durationTotal: 0, responseTotal: 0 },
                    data: []
                });
            }

            const department = map.get(departmentName);
            department.persons.push({
                name: getPersonName(person),
                fullName: getPersonName(person).trim().toLowerCase(),
                numbers: filteredNumbers
            });
            filteredNumbers.forEach(number => department.numbers.add(number));
        });

        return Array.from(map.values());
    }, [people, normalizedFilteredPosts]);

    useEffect(() => {
        handleGraphDepartmentSurvey(Array.isArray(data) ? data : []);
        setCurrentDepartmentPage({});
        setCurrentPage(1);
    }, [data, departments, filteredPosts, effectiveHoursFilter, classifyHours, hasSchedule]);

    const addCallToStats = (stats, item) => {
        stats.total += 1;
        stats.durationTotal += Math.floor(parseInt(item.duration) || 0);

        if (isBusyCall(item)) {
            stats.busy += 1;
        } else if (item.answerindicator === "Yes") {
            stats.response += 1;
            stats.averageresponse += Math.floor(parseInt(item.ringduration) || 0);
        } else if (item.answerindicator === "No") {
            stats.noresponse += 1;
        }
    };

    const itemBelongsToPostOutgoing = (item, postNum, personFullName) => {
        if (item.callingnumber !== postNum) return false;

        if (canUseCallerId && personFullName) {
            const itemName = (item.callinglineid || "").trim().toLowerCase();
            if (itemName && itemName !== personFullName) return false;
        }

        return true;
    };

    const itemBelongsToPostIncoming = (item, postNum) => (
        item.callednumber === postNum
    );

    const finalizeStats = (departmentData) => {
        departmentData.forEach(department => {
            const Outgoing = department.Outgoing;
            const Incoming = department.Incoming;

            Outgoing.averageduration = Outgoing.total > 0 ? Outgoing.durationTotal / Outgoing.total : 0;
            Outgoing.averageresponse = Outgoing.response > 0 ? Outgoing.averageresponse / Outgoing.response : 0;
            Incoming.averageduration = Incoming.total > 0 ? Incoming.durationTotal / Incoming.total : 0;
            Incoming.averageresponse = Incoming.response > 0 ? Incoming.averageresponse / Incoming.response : 0;

            department.Total.responseTotal = Outgoing.total + Incoming.total;
            department.Total.answeredTotal = Outgoing.response + Incoming.response;
            department.Total.durationTotal = formatDuration(Outgoing.durationTotal + Incoming.durationTotal);

            Outgoing.durationTotal = formatDuration(Outgoing.durationTotal);
            Outgoing.averageduration = formatDuration(Outgoing.averageduration);
            Outgoing.averageresponse = formatDuration(Outgoing.averageresponse);
            Incoming.durationTotal = formatDuration(Incoming.durationTotal);
            Incoming.averageduration = formatDuration(Incoming.averageduration);
            Incoming.averageresponse = formatDuration(Incoming.averageresponse);
        });
    };

    const handleGraphDepartmentSurvey = (calls) => {
        const departmentData = departments.map(department => ({
            ...department,
            numbers: new Set(department.numbers),
            persons: [...department.persons],
            Outgoing: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0, busy: 0 },
            Incoming: { total: 0, durationTotal: 0, averageduration: 0, response: 0, averageresponse: 0, noresponse: 0, busy: 0 },
            Total: { durationTotal: 0, responseTotal: 0 },
            data: []
        }));

        calls.forEach(item => {
            if (hasSchedule) {
                const reasons = [item.calloutcomereason, item.originalreason, item.redirectreason, item.relatedreason];
                const disconnected = reasons.some(value => ["unavailable", "temporarilyunavailable", "callforwardnotreachable"].includes(String(value || "").toLowerCase()));
                if (isBusyCall(item) || disconnected || (peopleByNumber.has(item.callingnumber) && peopleByNumber.has(item.callednumber))) return;
            }
            departmentData.forEach(department => {
                const classification = hasSchedule ? classifyHours(department.department, item.starttimeUtc) : null;
                if (effectiveHoursFilter !== "all" && classification?.bucket !== effectiveHoursFilter) return;
                const scheduledItem = classification ? {
                    ...item, scheduleReason: classification.reason,
                    starttime: classification.localDateTime || item.starttime
                } : item;
                const allowOutgoing = normalizedFilteredPosts.calltype !== ""
                    ? normalizedFilteredPosts.calltype.includes("sortants")
                    : true;
                const allowIncoming = normalizedFilteredPosts.calltype !== ""
                    ? normalizedFilteredPosts.calltype.includes("entrants")
                    : true;
                const matchOutgoingDirection = canUseCallerId ? item.direction === "ORIGINATING" : true;
                const matchIncomingDirection = canUseCallerId ? item.direction === "TERMINATING" : true;

                department.persons.forEach(person => {
                    person.numbers.forEach(number => {
                        if (allowOutgoing && matchOutgoingDirection && itemBelongsToPostOutgoing(item, number, person.fullName)) {
                            addCallToStats(department.Outgoing, item);
                            department.data.push({
                                ...scheduledItem,
                                departmentDirection: "Outgoing",
                                departmentPost: number,
                                departmentPersonName: person.name
                            });
                        }

                        if (allowIncoming && matchIncomingDirection && itemBelongsToPostIncoming(item, number)) {
                            addCallToStats(department.Incoming, item);
                            department.data.push({
                                ...scheduledItem,
                                departmentDirection: "Incoming",
                                departmentPost: number,
                                departmentPersonName: person.name
                            });
                        }
                    });
                });
            });
        });
        const filteredDepartmentData = departmentData
            .filter(department => department.Outgoing.total > 0 || department.Incoming.total > 0 || department.data.length > 0)
            .sort((a, b) => a.department.localeCompare(b.department, "fr", { sensitivity: "base" }));

        finalizeStats(filteredDepartmentData);
        setDepartmentSurvey(filteredDepartmentData);
        setDataGraph(generateExcelData(filteredDepartmentData));
    };

    const generateExcelData = (departmentsData) => {
        const formattedData = [["Departement", "Sortant", "Entrant", "Total", "", "", "", "", "", "", ""]];
        if (hasSchedule) formattedData.push(["Horaires", effectiveHoursFilter === "all" ? "Tous les horaires" : effectiveHoursFilter === "inside" ? "Pendant les horaires" : "Hors horaires"]);

        departmentsData.forEach(department => {
            formattedData.push([department.department, "", "", ""]);
            formattedData.push(["Personnes", department.persons.map(person => person.name).filter(Boolean).join(", "), "", ""]);
            formattedData.push(["Nombre d'appels", department.Outgoing.total || 0, department.Incoming.total || 0, ""]);
            formattedData.push(["Nombre d'appels repondu", department.Outgoing.response || 0, department.Incoming.response || 0, department.Total.answeredTotal || 0]);
            formattedData.push(["Duree totale d'appels", department.Outgoing.durationTotal || "00:00:00", department.Incoming.durationTotal || "00:00:00", department.Total.durationTotal || "00:00:00"]);
            formattedData.push(["Duree moyenne pour repondre", department.Outgoing.averageresponse || "00:00:00", department.Incoming.averageresponse || "00:00:00", ""]);
            formattedData.push(["Nombre d'appels non repondu", department.Outgoing.noresponse || 0, department.Incoming.noresponse || 0, ""]);
            formattedData.push(["Nombre d'appels Busy", department.Outgoing.busy || 0, department.Incoming.busy || 0, ""]);
            formattedData.push(["Duree moyenne d'appels", department.Outgoing.averageduration || "00:00:00", department.Incoming.averageduration || "00:00:00", ""]);
            formattedData.push(["Date", "Duree de communication", "Departement", "Poste", "Nom poste", "Numero", ...(canUseCallerId ? ["Numero affiche"] : []), "Nom", "Origine", "Resultat", "Duree d'attente", "Type", ...(hasSchedule ? ["Classement horaire"] : [])]);

            department.data.forEach(call => {
                formattedData.push([
                    convertDateWithDayName(call.starttime, currentLang) || "",
                    call.duration || "0",
                    department.department,
                    call.departmentPost || "",
                    call.departmentPersonName || "",
                    call.departmentDirection === "Outgoing" ? call.callednumber : call.callingnumber,
                    ...(canUseCallerId ? [call.calleridnumber || ""] : []),
                    call.departmentDirection === "Outgoing" ? call.calledlineid : call.callinglineid,
                    getCallType(call.calltype),
                    getCallResultLabel(call),
                    call.ringduration || "0",
                    getCallDirection(call.departmentDirection === "Outgoing" ? "ORIGINATING" : "TERMINATING"),
                    ...(hasSchedule ? [call.scheduleReason] : [])
                ]);
            });
            formattedData.push([]);
        });

        setFormattedHeader(true);
        return formattedData;
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const paginateDepartment = (department, pageNumber) => {
        setCurrentDepartmentPage(prev => ({ ...prev, [department]: pageNumber }));
    };

    const changeDepartmentItemPerPage = (change) => {
        setDepartmentItemsPerPage(prevValue => {
            const newValue = prevValue + change;
            if (newValue < 1) return 1;
            if (newValue > 15) return 15;
            return newValue;
        });
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    return (
        <div className="postSurvey">
            {Number(user?.entrepriseId) === 41 && (
                <div className="postSurvey-element">
                    {hoursQuery.isLoading && <p>{t("Chargement des horaires...")}</p>}
                    {hoursQuery.isError && <p role="alert">{t("Horaires indisponibles. Le rapport est affiche sans filtre horaire.")}</p>}
                    {hasSchedule && <>
                        <label>
                            {t("Horaires du departement")} : {" "}
                            <select value={hoursFilter} onChange={event => setHoursFilter(event.target.value)}>
                                <option value="all">{t("Tous les horaires")}</option>
                                <option value="inside">{t("Pendant les horaires")}</option>
                                <option value="outside">{t("Hors horaires")}</option>
                            </select>
                        </label>
                        <p>{t("Appels internes, Busy et indisponibles exclus. Les departements sans planning restent sans restriction horaire.")}</p>
                    </>}
                </div>
            )}
            {departmentSurvey.length > 0 ? (
                <>
                    <div className="pagination-surveyPost">
                        <button
                            onClick={() => paginate(currentPage > 1 ? currentPage - 1 : currentPage)}
                            disabled={currentPage === 1}
                        >
                            {t("Precedent")}
                        </button>
                        <button
                            onClick={() => paginate(currentPage < Math.ceil(departmentSurvey.length / itemsPerPage) ? currentPage + 1 : currentPage)}
                            disabled={currentPage === Math.ceil(departmentSurvey.length / itemsPerPage)}
                        >
                            {t("Suivant")}
                        </button>
                    </div>
                    {departmentSurvey.slice(indexOfFirstItem, indexOfLastItem).map(department => {
                        const page = currentDepartmentPage[department.department] || 1;
                        const details = department.data.slice(
                            page * departmentItemsPerPage - departmentItemsPerPage,
                            page * departmentItemsPerPage
                        );

                        return (
                            <section className="postSurvey-element" key={department.department}>
                                <div>
                                    <table className="postSurvey-sortants">
                                        <thead>
                                            <tr className="sortant"><th colSpan={7}>{t("Sortants")}</th></tr>
                                            <tr className="sortant-head">
                                                <th title={t("Departement")}>{t("Departement")}</th>
                                                <th title={t("Nombre d'appels repondu")}>{t("Nombre d'appels repondu")}</th>
                                                <th title={t("Duree moyenne pour repondre")}>{t("Duree moyenne pour repondre")}</th>
                                                <th title={t("Nombre d'appels non repondu")}>{t("Nombre d'appels non repondu")}</th>
                                                <th title={t("Nombre d'appels Busy")}>{t("Nombre d'appels Busy")}</th>
                                                <th title={t("Duree totale d'appels")}>{t("Duree totale d'appels")}</th>
                                                <th title={t("Duree moyenne d'appels")}>{t("Duree moyenne d'appels")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="sortant-body">
                                                <td>{department.department}</td>
                                                <td>{department.Outgoing.response}</td>
                                                <td>{department.Outgoing.averageresponse}</td>
                                                <td>{department.Outgoing.noresponse}</td>
                                                <td>{department.Outgoing.busy}</td>
                                                <td>{department.Outgoing.durationTotal}</td>
                                                <td>{department.Outgoing.averageduration}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <table className="postSurvey-entrants">
                                        <thead>
                                            <tr className="entrant"><th colSpan={6}>{t("Entrants")}</th></tr>
                                            <tr>
                                                <th title={t("Nombre d'appels repondu")}>{t("Nombre d'appels repondu")}</th>
                                                <th title={t("Duree moyenne pour repondre")}>{t("Duree moyenne pour repondre")}</th>
                                                <th title={t("Nombre d'appels non repondu")}>{t("Nombre d'appels non repondu")}</th>
                                                <th title={t("Nombre d'appels Busy")}>{t("Nombre d'appels Busy")}</th>
                                                <th title={t("Duree totale d'appels")}>{t("Duree totale d'appels")}</th>
                                                <th title={t("Duree moyenne d'appels")}>{t("Duree moyenne d'appels")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{department.Incoming.response}</td>
                                                <td>{department.Incoming.averageresponse}</td>
                                                <td>{department.Incoming.noresponse}</td>
                                                <td>{department.Incoming.busy}</td>
                                                <td>{department.Incoming.durationTotal}</td>
                                                <td>{department.Incoming.averageduration}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <table className="postSurvey-total">
                                        <thead className="postSurvey-total-head">
                                            <tr className="total"><th colSpan={5}>{t("Total")}</th></tr>
                                            <tr>
                                                <th title={t("Duree totale des appels")}>{t("Duree totale des appels")}</th>
                                                <th title={t("Nombre total d'appels")}>{t("Nombre total d'appels")}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="postSurvey-total-body">
                                            <tr>
                                                <td>{department.Total.durationTotal}</td>
                                                <td>{department.Total.responseTotal}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <p className="postSurvey-post-title">{t("Personnes du departement")} : {department.persons.map(person => person.name).filter(Boolean).join(", ")}</p>
                                <p className="postSurvey-post-title">{t("Details des appels")} :</p>
                                <div>
                                    <table className="postSurvey-post">
                                        <thead>
                                            <tr className="postSurvey-post-head">
                                                <th>{t("Date")}</th>
                                                <th>{t("Duree de communication")}</th>
                                                <th>{t("Poste")}</th>
                                                <th>{t("Nom poste")}</th>
                                                <th>{t("Numero")}</th>
                                                {canUseCallerId && <th>{t("Numero affiche")}</th>}
                                                <th>{t("Nom")}</th>
                                                <th>{t("Origine")}</th>
                                                <th>{t("Resultat")}</th>
                                                <th title={t("Duree d'attente")}>{t("Duree d'attente")}</th>
                                                <th>{t("Type")}</th>
                                                {hasSchedule && <th>{t("Classement horaire")}</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {details.map((item, idx) => (
                                                    <tr className="postSurvey-post-body" key={`${department.department}-${idx}`}>
                                                        <td>{convertDateWithDayName(item.starttime, currentLang)}</td>
                                                        <td>{item.duration}</td>
                                                        <td>{item.departmentPost}</td>
                                                        <td>{item.departmentPersonName}</td>
                                                        <td>{item.departmentDirection === "Outgoing" ? item.callednumber : item.callingnumber}</td>
                                                        {canUseCallerId && <td>{item.calleridnumber}</td>}
                                                        <td>{item.departmentDirection === "Outgoing" ? item.calledlineid : item.callinglineid}</td>
                                                        <td>{getCallType(item.calltype)}</td>
                                                        <td>{getCallResultLabel(item)}</td>
                                                        <td>{item.ringduration}</td>
                                                        <td>{getCallDirection(item.departmentDirection === "Outgoing" ? "ORIGINATING" : "TERMINATING")}</td>
                                                        {hasSchedule && <td>{item.scheduleReason}</td>}
                                                    </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="pagination-surveyPost-post">
                                    <button
                                        onClick={() => paginateDepartment(department.department, page > 1 ? page - 1 : 1)}
                                        disabled={page === 1}
                                    >
                                        {t("Precedent")}
                                    </button>
                                    <button
                                        onClick={() => paginateDepartment(department.department, page < Math.ceil(department.data.length / departmentItemsPerPage) ? page + 1 : page)}
                                        disabled={page === Math.ceil(department.data.length / departmentItemsPerPage)}
                                    >
                                        {t("Suivant")}
                                    </button>
                                    <div className="surveyPost-post-numbers">
                                        <button onClick={() => changeDepartmentItemPerPage(-1)} disabled={departmentItemsPerPage === 1}>-</button>
                                        <span>{departmentItemsPerPage}</span>
                                        <button onClick={() => changeDepartmentItemPerPage(+1)} disabled={departmentItemsPerPage === 15}>+</button>
                                    </div>
                                </div>
                            </section>
                        );
                    })}
                </>
            ) : (
                <div>{t("Aucune donnee disponible")}</div>
            )}
        </div>
    );
}
